// Fits a Web Mercator viewport around a set of points and projects each one
// to a percentage offset inside the map image. The export draws the app's own
// SVG pictos on top of a marker-less Static Maps image (KD-3462): Static Maps
// cannot render SVG icons, and shipping raster copies would fork the pictos
// away from @koob/map-marker.
const TILE_SIZE = 256;

// Lille, kept from the interactive map's behaviour when a day carries no point.
const FALLBACK_CENTER = { lat: 50.633, lon: 3.066 };
const FALLBACK_ZOOM = 12;

// A lone point would otherwise zoom to the street; the interactive map capped
// at 16, 13 reads better on a printed page.
const SINGLE_POINT_ZOOM = 13;

// Keeps fitted markers away from the image edges, where the container's
// object-cover crop could swallow them.
const FIT_PADDING_PX = 64;
const MAX_FIT_ZOOM = 13;

function toCoordinates(points) {
  return (points ?? [])
    .map((point) => ({
      lat: parseFloat('' + point?.lat),
      lon: parseFloat('' + point?.lon),
      point,
    }))
    .filter(({ lat, lon }) => Number.isFinite(lat) && Number.isFinite(lon));
}

// Both in world units of [0, 1]; y grows southward, as tiles do.
function project({ lat, lon }) {
  const phi = (Math.max(-85, Math.min(85, lat)) * Math.PI) / 180;
  return {
    x: (lon + 180) / 360,
    y: (1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2,
  };
}

function unprojectLat(y) {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
}

export default function staticMapView(points, { width, height }) {
  const coordinates = toCoordinates(points);

  if (coordinates.length === 0) {
    return { center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM, markers: [] };
  }

  const projected = coordinates.map(project);
  const min = {
    x: Math.min(...projected.map((p) => p.x)),
    y: Math.min(...projected.map((p) => p.y)),
  };
  const max = {
    x: Math.max(...projected.map((p) => p.x)),
    y: Math.max(...projected.map((p) => p.y)),
  };
  const mid = { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2 };
  const center = { lat: unprojectLat(mid.y), lon: mid.x * 360 - 180 };

  let zoom = SINGLE_POINT_ZOOM;
  if (coordinates.length > 1) {
    // Largest integer zoom whose padded world span still fits both
    // dimensions. Static Maps only accepts integer zooms, and flooring can
    // only widen the view, so every marker stays inside the padding.
    const spanX = Math.max(max.x - min.x, Number.EPSILON);
    const spanY = Math.max(max.y - min.y, Number.EPSILON);
    zoom = Math.min(
      MAX_FIT_ZOOM,
      Math.floor(
        Math.min(
          Math.log2((width - 2 * FIT_PADDING_PX) / (TILE_SIZE * spanX)),
          Math.log2((height - 2 * FIT_PADDING_PX) / (TILE_SIZE * spanY)),
        ),
      ),
    );
  }
  zoom = Math.max(0, zoom);

  const worldSize = TILE_SIZE * 2 ** zoom;
  const markers = coordinates.map((coordinate, index) => {
    const p = projected[index];
    return {
      ...coordinate,
      leftPct: ((width / 2 + (p.x - mid.x) * worldSize) / width) * 100,
      topPct: ((height / 2 + (p.y - mid.y) * worldSize) / height) * 100,
    };
  });

  return { center, zoom, markers };
}
